import type { ProjectTask, Subtask, TaskStatus } from '../types';

export function buildChildTasksByParent(tasks: ProjectTask[]): Map<string, ProjectTask[]> {
  const map = new Map<string, ProjectTask[]>();
  for (const t of tasks) {
    if (!t.parentTaskId || !t.id) continue;
    const list = map.get(t.parentTaskId) ?? [];
    list.push(t);
    map.set(t.parentTaskId, list);
  }
  Array.from(map.values()).forEach((list) => {
    list.sort((a, b) => (a.position || 0) - (b.position || 0));
  });
  return map;
}

export function topLevelProjectTasks(tasks: ProjectTask[]): ProjectTask[] {
  return tasks.filter((t) => !t.parentTaskId);
}

/** Parents that still have legacy nested subtasks[] but no child ProjectTask rows yet. */
export function tasksNeedingSubtaskMigration(tasks: ProjectTask[]): ProjectTask[] {
  const parentIdsWithChildren = new Set(
    tasks.filter((t) => t.parentTaskId).map((t) => t.parentTaskId!),
  );
  return tasks.filter(
    (t) =>
      t.id &&
      !t.parentTaskId &&
      (t.subtasks?.length ?? 0) > 0 &&
      !parentIdsWithChildren.has(t.id),
  );
}

function subtaskStatus(completed: boolean): TaskStatus {
  return completed ? 'completed' : 'pending';
}

/** One calendar day at the parent's start — default span for new subtasks. */
export function defaultSubtaskDateRange(parent: ProjectTask): { startDate: Date; endDate: Date } {
  const startDate = new Date(parent.startDate);
  startDate.setHours(0, 0, 0, 0);
  const endDate = new Date(startDate);
  return { startDate, endDate };
}

export function legacySubtaskToChildPayload(
  parent: ProjectTask,
  sub: Subtask,
): Omit<ProjectTask, 'id' | 'createdAt' | 'updatedAt'> {
  return {
    projectId: parent.projectId,
    groupId: parent.groupId,
    parentTaskId: parent.id!,
    title: sub.title,
    department: parent.department,
    assignedTo: sub.assignedTo ? [sub.assignedTo] : [...(parent.assignedTo || [])],
    status: subtaskStatus(sub.completed),
    priority: parent.priority,
    ...defaultSubtaskDateRange(parent),
    dependencies: [],
    progress: sub.completed ? 100 : 0,
    position: sub.position,
  };
}

export type CreateProjectTaskFn = (
  task: Omit<ProjectTask, 'id' | 'createdAt' | 'updatedAt'>,
) => Promise<string>;

export type UpdateProjectTaskFn = (id: string, updates: Partial<ProjectTask>) => Promise<void>;

/** Convert legacy nested subtasks[] on a parent into child ProjectTask documents. */
export async function migrateLegacySubtasksForParent(
  parent: ProjectTask,
  createProjectTask: CreateProjectTaskFn,
  updateProjectTask: UpdateProjectTaskFn,
): Promise<void> {
  if (!parent.id || !parent.subtasks?.length) return;
  for (const sub of [...parent.subtasks].sort((a, b) => a.position - b.position)) {
    await createProjectTask(legacySubtaskToChildPayload(parent, sub));
  }
  await updateProjectTask(parent.id, { subtasks: [] });
}
